import {
    computed,
    isSignal,
    type InputSignal,
    type Signal,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import type { AbstractControl, ControlEvent } from '@angular/forms';
import {
    PristineChangeEvent,
    StatusChangeEvent,
    TouchedChangeEvent,
    ValueChangeEvent,
    type FormControlStatus,
    type ValidationErrors,
} from '@angular/forms';
import type { Observable } from 'rxjs';
import {
    defer,
    distinctUntilChanged,
    filter,
    map,
    of,
    startWith,
    switchMap,
} from 'rxjs';

function valueEvents$<T>(form: AbstractControl<T>) {
    return form.events.pipe(
        filter(
            (e: ControlEvent): e is ValueChangeEvent<typeof form.value> =>
                e instanceof ValueChangeEvent
        )
    );
}

function statusEvents$<T>(form: AbstractControl<T>) {
    return form.events.pipe(
        filter(
            (e: ControlEvent): e is StatusChangeEvent =>
                e instanceof StatusChangeEvent
        )
    );
}

function touchedEvents$<T>(form: AbstractControl<T>) {
    return form.events.pipe(
        filter(
            (e: ControlEvent): e is TouchedChangeEvent =>
                e instanceof TouchedChangeEvent
        )
    );
}

function pristineEvents$<T>(form: AbstractControl<T>) {
    return form.events.pipe(
        filter(
            (e: ControlEvent): e is PristineChangeEvent =>
                e instanceof PristineChangeEvent
        )
    );
}

function isValueEvent<T>(e: ControlEvent | T): e is ValueChangeEvent<T> {
    return e instanceof ValueChangeEvent;
}
function isStatusEvent<T>(e: ControlEvent | T): e is StatusChangeEvent {
    return e instanceof StatusChangeEvent;
}
function isPristineEvent<T>(e: ControlEvent | T): e is PristineChangeEvent {
    return e instanceof PristineChangeEvent;
}
function isTouchedEvent<T>(e: ControlEvent | T): e is TouchedChangeEvent {
    return e instanceof TouchedChangeEvent;
}

interface FormEventData<T> {
    // These values are possible in Angular 16, see links at the bottom
    value: T;
    status: FormControlStatus;
    valid: boolean;
    invalid: boolean;
    pending: boolean;
    touched: boolean;
    pristine: boolean;
    dirty: boolean;
    untouched: boolean;
    errors: ValidationErrors | null;
    disabled: boolean;
}

export interface CreateField<T> {
    value: Signal<T>;
    status: Signal<FormControlStatus>;
    valid: Signal<boolean>;
    invalid: Signal<boolean>;
    pending: Signal<boolean>;
    touched: Signal<boolean>;
    pristine: Signal<boolean>;
    dirty: Signal<boolean>;
    untouched: Signal<boolean>;
    errors: Signal<ValidationErrors | null>;
    disabled: Signal<boolean>;
}

function createControlSignal<T>(
    control$: Observable<AbstractControl<unknown>>,
    pipeFn: (c: AbstractControl<unknown>) => Observable<T>,
    initialValue: T
): Signal<T> {
    return toSignal(
        defer(() => control$.pipe(switchMap((c) => pipeFn(c)))),
        { initialValue }
    );
}

/**
 * Inspired by: https://www.angularspace.com/brewing-bootleg-reactive-forms-signals-from-rxjs/
 */
export function createField<T>(
    control:
        | InputSignal<AbstractControl<unknown>>
        | Signal<AbstractControl<unknown>>,
    initialValue: T
): CreateField<T>;
export function createField<T>(
    control: AbstractControl<unknown>,
    initialValue?: T
): CreateField<T>;
export function createField<T>(
    control:
        | AbstractControl<unknown>
        | InputSignal<AbstractControl<unknown>>
        | Signal<AbstractControl<unknown>>,
    initialValue?: T
): CreateField<T> {
    const control$ = isSignal(control) ? toObservable(control) : of(control);
    const defaultState: Omit<FormEventData<T>, 'value'> = {
        status: 'VALID',
        valid: true,
        invalid: false,
        pending: false,
        touched: false,
        pristine: true,
        dirty: false,
        untouched: true,
        errors: null,
        disabled: false,
    };
    const value = createControlSignal(
        control$,
        (c) =>
            valueEvents$<T>(c as AbstractControl<T>).pipe(
                startWith(c.getRawValue()),
                map(() => c.getRawValue()),
                distinctUntilChanged(
                    (previous, current) =>
                        JSON.stringify(previous) === JSON.stringify(current)
                )
            ),
        isSignal(control) ? initialValue : (initialValue ?? control.value)
    ) as Signal<T>;

    const status = createControlSignal(
        control$,
        (c) =>
            statusEvents$(c).pipe(
                startWith(c.status),
                map((status) =>
                    isStatusEvent(status) ? status.status : status
                ),
                distinctUntilChanged()
            ),
        isSignal(control) ? defaultState.status : control.status
    );

    const touched = createControlSignal(
        control$,
        (c) =>
            touchedEvents$(c).pipe(
                startWith(c.touched),
                map((touched) =>
                    isTouchedEvent(touched) ? touched.touched : touched
                ),
                distinctUntilChanged()
            ),
        isSignal(control) ? defaultState.touched : control.touched
    );

    const pristine = createControlSignal(
        control$,
        (c) =>
            pristineEvents$(c).pipe(
                startWith(c.pristine),
                map((pristine) =>
                    isPristineEvent(pristine) ? pristine.pristine : pristine
                ),
                distinctUntilChanged()
            ),
        isSignal(control) ? defaultState.pristine : control.pristine
    );

    const errors = createControlSignal(
        control$,
        (c) =>
            statusEvents$(c).pipe(
                startWith(c.errors),
                map(() => c.errors)
            ),
        isSignal(control) ? defaultState.errors : control.errors
    );

    const disabled = createControlSignal(
        control$,
        (c) =>
            statusEvents$(c).pipe(
                startWith(c.disabled),
                map(() => c.disabled)
            ),
        isSignal(control) ? defaultState.disabled : control.disabled
    );

    const valid = computed(() => status() === 'VALID');
    const invalid = computed(() => status() === 'INVALID');
    const pending = computed(() => status() === 'PENDING');
    const dirty = computed(() => !pristine());
    const untouched = computed(() => !touched());

    return {
        value,
        status,
        touched,
        pristine,
        valid,
        invalid,
        pending,
        dirty,
        untouched,
        errors,
        disabled,
    };
}
